import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useMemo } from "react";

import { useSelectedTimeRange } from "@/features/dashboard/store";
import { ParameterTimeSeriesChartResult } from "@/lib/api";
import { BLUE, colorGenerator, GREEN } from "@/features/shared/widgets/graphs/helpers";
import { renderValue } from "@/features/dashboard/components/timeseries/render-value";
import { DimensionValue } from "@/lib/dimensions";
import { getYAxisPerUnit } from "@/components/graph/get-y-axis-for-unit";
import { NoDataOverlay } from "@/features/shared/widgets/graphs/no-data-label";
import {
  chartSeriesIsDisabled,
  chartSeriesPresentationColor,
  formatChartSeriesName,
  getChartSeriesPresentationState,
  resolveSeriesDisplayLabel,
} from "@/components/graph/chart-series-presentation";
import { useTranslation } from "@/lib/i18n";

import useGraphOptions from "./use-graph-options";
import { resolveGraphChartHeight } from "./resolve-graph-chart-height";
import { useTimeSeriesNoData } from "./hooks/use-time-series-no-data";
import type { ChartTimeBounds } from "./get-shared-chart-time-bounds";
import { ScatterplotTimeSeriesConfig } from "./graph.types";
import {
  buildThresholdPlotBands,
  buildThresholdPlotLines,
  buildThresholdZones,
} from "./thresholds";

interface ScatterplotProps {
  config: ScatterplotTimeSeriesConfig;
  data: ParameterTimeSeriesChartResult[];
  currentTime: DimensionValue;
  timeBounds?: ChartTimeBounds;
  height?: number;
}

const Scatterplot = ({
  config,
  data,
  currentTime,
  timeBounds,
  height,
}: ScatterplotProps) => {
  const { t } = useTranslation();
  const timeRange = useSelectedTimeRange();

  const thresholdZones = useMemo(
    () => buildThresholdZones(config.thresholds),
    [config.thresholds],
  );
  const thresholdPlotLines = useMemo(
    () => buildThresholdPlotLines(config.thresholds),
    [config.thresholds],
  );
  const thresholdPlotBands = useMemo(
    () => buildThresholdPlotBands(config.thresholds, data),
    [config.thresholds, data],
  );

  const seriesWithData = useMemo<Highcharts.SeriesOptionsType[]>(() => {
    const greenColorGenerator = colorGenerator(GREEN);
    const blueColorGenerator = colorGenerator(BLUE);
    const getColor = (color: string) => {
      const fallback = "grey";
      if (color === "green") {
        return greenColorGenerator.next().value ?? fallback;
      }
      if (color === "blue") {
        return blueColorGenerator.next().value ?? fallback;
      }
      return fallback;
    };

    return config.series.map((seriesConfig) => {
      const dataByParameter = data.find(
        (timeSeriesResult) => timeSeriesResult.parameter.id === seriesConfig.parameter_id,
      );
      const presentationState = getChartSeriesPresentationState(dataByParameter, timeRange);
      const displayLabel = resolveSeriesDisplayLabel(seriesConfig, dataByParameter);
      const serieName = formatChartSeriesName(displayLabel, presentationState, t);
      const datapoints = dataByParameter?.data?.datapoints ?? [];
      const mappedDatapoints = datapoints.map((datapoint) => ({
        x: +new Date(datapoint.timestamp),
        y: datapoint.value,
      }));

      return {
        // base configuration
        type: "line",
        name: serieName,

        // line style configuration
        color: chartSeriesPresentationColor(
          presentationState,
          getColor(seriesConfig.color),
        ),
        lineWidth: 0,
        dashStyle: "Solid",
        enableMouseTracking: !chartSeriesIsDisabled(presentationState),
        marker: {
          symbol: seriesConfig.symbol,
          radius: 4,
          enabledThreshold: 0,
        },
        tooltip: {
          pointFormatter: function (this) {
            let symbol = "";
            // @ts-expect-error symbol actually exists
            switch (this.graphic.symbol) {
              case "circle":
                symbol = "&#9679";
                break;
              case "triangle":
                symbol = "&#9650";
                break;
              case "triangle-down":
                symbol = "&#9660";
                break;
              case "diamond":
                symbol = "&#9670";
                break;
              case "square":
              default:
                symbol = "&#9632";
                break;
            }
            return `<span style="color: ${this.color}">${symbol}</span> ${
              this.series.name
            }: <strong>${renderValue(this.y, config.unit)}</strong>`;
          },
        },
        states: {
          hover: {
            lineWidthPlus: 0,
          },
        },
        zones: thresholdZones,

        // data configuration
        data: mappedDatapoints,
      };
    });
  }, [config, data, timeRange, t, thresholdZones]);

  const yAxisPerUnit = getYAxisPerUnit(config.unit);
  const chartHeight = resolveGraphChartHeight(height);
  const graphOptions = useGraphOptions(
    {
      ...(chartHeight !== undefined ? { chart: { height: chartHeight } } : {}),
      title: {
        text: config.title,
      },
      yAxis: [
        { ...(yAxisPerUnit[0] ?? {}), plotLines: thresholdPlotLines },
        ...yAxisPerUnit.slice(1),
      ],
      xAxis: [
        {
          plotLines: [
            {
              id: "currentTime",
              color: "#01689B", // KNMI blue
              value: +new Date(currentTime.queryStringValue),
              // Sit above the threshold fill bands (zIndex 0) but below the data series (zIndex 3).
              zIndex: 2,
            },
          ],
          plotBands: thresholdPlotBands,
        },
      ],
      series: seriesWithData,
    },
    timeBounds,
  );

  const noData = useTimeSeriesNoData(config, data);

  return (
    <NoDataOverlay active={noData}>
      <HighchartsReact highcharts={Highcharts} options={graphOptions} />
    </NoDataOverlay>
  );
};

export default Scatterplot;
